import EmptyState from '@/components/DataTable/EmptyState';
import { FlatList, Platform, StyleSheet } from 'react-native';
import { BaseDataItemType, DataTableProps } from '@/components/DataTable/DataTable';
import { useConfigurationContext } from '@/components/Contexts/ConfigurationContext';
import { DataTable } from 'react-native-paper';
import ListItem from '@/components/DataTable/ListItem';
import { Color, FontFamily, FontSize } from '@/GlobalStyles';
import { DataTableTitle } from '@/components/CustomRNPaper/DataTableTitle';
import { useMemo } from 'react';
import useNumberOfColumns from '@/hooks/useNumberOfColumns';

const List = <T extends BaseDataItemType>(props: DataTableProps<T>) => {
  const {
    data,
    headers,
    keyExtractor,
    sortByState,
    onHeaderPress,
    loading,
    forceListType,
    forceNumberOfColumns,
  } = props;
  const { listType: configListType } = useConfigurationContext();

  const listType = forceListType || configListType;

  const configNumberOfColumns = useNumberOfColumns(listType, true);

  const numberOfColumns = forceNumberOfColumns || configNumberOfColumns;

  const finalHeaders = useMemo(() => {
    return (
      headers &&
      headers.filter((item) => {
        return item !== null;
      })
    );
  }, [headers]);

  let columnWrapperStyleProp = {};
  if (numberOfColumns > 1) {
    columnWrapperStyleProp = {
      contentContainerStyle: {},
    };
  }

  const Header = useMemo(() => {
    if (listType === 'card' || (!loading && (!data || data.length === 0)) || !finalHeaders) {
      return null;
    }
    return (
      <DataTable.Header style={{ backgroundColor: Color.blanc }}>
        {finalHeaders.map((headerItem) => {
          if (!headerItem) {
            return null;
          }
          const { sortable, ...item } = headerItem;
          let sortDirection = undefined;
          if (sortable && sortByState && sortByState.key === item.key) {
            sortDirection = sortByState.sort;
          }

          return (
            <DataTableTitle
              key={`header_${String(item.key)}`}
              textStyle={[
                styles.headerStyle,
                sortDirection ? styles.headerSortedStyle : null,
                Platform.OS === 'web'
                  ? {
                      cursor: 'pointer',
                    }
                  : null,
              ]}
              sortDirection={sortDirection}
              style={[styles.headerFieldContainer, item.style]}
              onPress={() => {
                if (sortable && onHeaderPress) {
                  onHeaderPress(item);
                }
              }}
            >
              {item.title}
            </DataTableTitle>
          );
        })}
      </DataTable.Header>
    );
  }, [finalHeaders, sortByState, onHeaderPress, listType]);

  return (
    <DataTable style={{ flex: 1 }}>
      {Header}
      <FlatList<T>
        data={loading && (data?.length || 0) <= 0 ? generatePlaceholder(props) : data}
        key={numberOfColumns}
        keyExtractor={loading ? (item, index) => index.toString() : keyExtractor}
        renderItem={({ item }) => {
          if (listType === 'card' && props.renderCard) {
            return props.renderCard(
              { ...item, loading: loading && (data?.length || 0) <= 0 },
              numberOfColumns,
              props.onItemPress
            );
          } else {
            return (
              <ListItem<T>
                item={item}
                {...props}
                headers={finalHeaders}
                loading={loading && (data?.length || 0) <= 0}
              />
            );
          }
        }}
        {...columnWrapperStyleProp}
        numColumns={numberOfColumns}
        ListEmptyComponent={() => {
          return loading ? null : <EmptyState {...props} />;
        }}
        style={[
          {  marginBottom: 30 },
          listType === 'card' ? { marginHorizontal: -10 } : null,
        ]}
      />
    </DataTable>
  );
};

const generatePlaceholder = <T extends BaseDataItemType>(props: DataTableProps<T>) => {
  if (!props.headers) {
    return [];
  }
  const data: T = {} as T;
  props.headers
    .filter((item) => item !== null)
    .forEach((item) => {
      // @ts-ignore
      data[item.key] = 'Chargement';
    });
  return [data, data, data, data, data];
};

export default List;

const styles = StyleSheet.create({
  headerStyle: {
    fontSize: FontSize.smallCapRegular_size,
    fontFamily: FontFamily.labelMedium,
    textTransform: 'uppercase',
    color: Color.MainNoir,
  },
  headerSortedStyle: {
    color: Color.MainBleu,
  },
  headerFieldContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
